import { Check, X } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, Eyebrow } from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { IMAGES } from "@/lib/site";

const fits = [
  "Business owners who sell high-consideration services (clients who think before they buy)",
  "People who believe their work genuinely helps others",
  "Local businesses in Manatee, Sarasota, Hillsborough, Pinellas County and surrounding areas",
  "Owners ready to show up on video and lead with their real voice",
];
const notFits = [
  "You want set-it-and-forget-it ads with no collaboration",
  "You're not willing to be the face of your brand",
  "You're looking for the cheapest option",
];

export default function About() {
  return (
    <>
      <Seo
        title="About Prolific Maven | Local Marketing Agency | Parrish, FL"
        description="Prolific Maven helps local Florida business owners clarify, refine & amplify their voice. Based in Parrish, FL, serving the greater Tampa Bay area."
        path="/about"
      />
      <PageHero
        eyebrow="About"
        title="We believe your voice is one of the most powerful business assets you own."
        subtitle="Most people never fully step into it. We're here to change that."
      />

      <section className="relative z-10 bg-ink-800 border-y border-white/10 py-24 sm:py-32" data-testid="about-story">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow>The Story</Eyebrow></Reveal>
            <Reveal delay={80}>
              <h2 className="mt-5 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">Why Prolific Maven exists</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={120}>
              <div className="space-y-6 text-lg text-cream/70 leading-relaxed">
                <p>We started Prolific Maven because we kept seeing the same thing: business owners who were genuinely excellent at what they did, but completely invisible in their market. Not because they lacked skill. Not because their product wasn't good enough. But because they hadn't found a way to let the world hear them.</p>
                <p>We built a process to fix that, starting with identity, moving through message, and ending with a local ad presence that actually works. We don't just run ads. We help you <span className="text-gold">clarify</span> who you are, <span className="text-gold">refine</span> how you say it, and <span className="text-gold">amplify</span> it across your entire local market.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative z-10 bg-ink-900 py-24 sm:py-32" data-testid="about-who">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid md:grid-cols-2 gap-8">
          <Reveal className="bg-ink-800 border border-white/5 p-8 sm:p-10">
            <h3 className="font-display font-bold text-2xl text-white">We work best with...</h3>
            <ul className="mt-6 space-y-4">
              {fits.map((f) => (
                <li key={f} className="flex gap-3 text-cream/75 leading-relaxed">
                  <Check className="text-gold shrink-0 mt-1" size={20} />{f}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120} className="bg-ink-800 border border-white/5 p-8 sm:p-10">
            <h3 className="font-display font-bold text-2xl text-white">We're probably not a fit if...</h3>
            <ul className="mt-6 space-y-4">
              {notFits.map((f) => (
                <li key={f} className="flex gap-3 text-cream/60 leading-relaxed">
                  <X className="text-cream/30 shrink-0 mt-1" size={20} />{f}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 bg-ink-800 border-y border-white/10 py-24 sm:py-32" data-testid="about-founder">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <img src={IMAGES.quill} alt="Silhouette of a hand writing with a quill, Prolific Maven, founder-led from Parrish, Florida" className="w-full max-w-sm border border-ink-line" loading="lazy" />
              <div className="absolute -inset-3 border border-gold/20 -z-10" />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal delay={100}><Eyebrow>The Founder</Eyebrow></Reveal>
            <Reveal delay={160}>
              <h2 className="mt-5 font-display font-extrabold text-3xl sm:text-4xl text-white">Alejo</h2>
              <p className="mt-2 text-gold">Based in Parrish, FL, serving the greater Tampa Bay area</p>
              <p className="mt-6 text-lg text-cream/70 leading-relaxed max-w-2xl">
                Prolific Maven is founder-led by design. The owner's voice and presence is the brand, and that's exactly the standard we hold our clients to. We help you lead with your real voice, because that's the one thing no competitor can ever copy.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <CTASection headline="If this resonates, we'd love to hear about your business." subhead="Tell us who you are and what you do. If we're a fit, we'll map out what working together looks like." />
    </>
  );
}
